import * as AriaKit from '@ariakit/react'
import * as React from 'react'

import { Input, InputHTMLProps, InputProps } from './input'
import { Tooltip, TooltipColor, TooltipProps, TooltipTrigger, useTooltipState } from './tooltip'

type TooltipInputProps = InputProps & InputHTMLProps & { isInteractive?: boolean }

interface TooltipSettingsProps extends Pick<TooltipProps, 'gutter'>, Pick<AriaKit.TooltipStoreProps, 'placement'> {
  content?: React.ReactNode
  color?: keyof typeof TooltipColor
}

export interface TooltipInputWrapperProps extends Pick<InputHTMLProps, 'name'> {
  children: React.ReactElement<TooltipInputProps>
  /**
   * Settings for Tooltip component and useTooltipStore
   */
  tooltipSettings?: TooltipSettingsProps
}

function TooltipInputWrapper({ children, tooltipSettings = {}, name }: TooltipInputWrapperProps) {
  const { content, color, gutter, placement = 'bottom' } = tooltipSettings
  const tooltip = useTooltipState({ placement })

  return (
    <>
      <TooltipTrigger focusable state={tooltip} tabIndex={-1} isHideOnClickOutside>
        {React.cloneElement(children, {
          onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
            children.props.onFocus?.(event)
            tooltip.show()
          },
          onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
            children.props.onBlur?.(event)
            tooltip.hide()
          },
        })}
      </TooltipTrigger>
      {content && (
        <Tooltip
          hideOnHoverOutside={false}
          className="px-2.5"
          id={name}
          state={tooltip}
          color={color}
          data-testid={name?.length ? `${name}-tooltip` : undefined}
          gutter={gutter}
        >
          {content}
        </Tooltip>
      )}
    </>
  )
}

function TooltipInputComponent(
  { tooltipSettings, isInteractive = true, ...props }: TooltipInputProps & Omit<TooltipInputWrapperProps, 'children'>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const input = <Input {...props} ref={ref} />

  if (!isInteractive) {
    return input
  }

  return (
    <TooltipInputWrapper name={props.name} tooltipSettings={tooltipSettings}>
      {input}
    </TooltipInputWrapper>
  )
}

export const TooltipInput = React.forwardRef(TooltipInputComponent)
